import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionHeader from "@/components/site/SectionHeader";
import BrandStripe from "@/components/site/BrandStripe";
import { GoodTagline } from "@/components/site/GoodTagline";
import useSEO from "@/hooks/useSEO";

export default function Contact() {
  useSEO({
    title: "Contact",
    description: "Get in touch with Good Eyes. Whether you'd like to become a stockist, are chasing product info or want to chat readers, we'd love to hear from you.",
    path: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !/\S+@\S+\.\S+/.test(form.email) || !form.message) {
      toast.error("Please add your name, a valid email, and a message.");
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Request failed");
      }
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-[color:var(--ge-navy)]" data-testid="contact-page">
      <Nav />
      <main>
        <section className="relative pt-40 md:pt-48 pb-24">
          <div className="ge-container grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-6">
              <SectionHeader
                kicker="Contact"
                title={<>Say <span style={{ color: "var(--ge-red)" }}>hello</span>.</>}
                subtitle="Whether you'd like to become a stockist, are chasing product info or just want to chat readers, we'd love to hear from you."
              />

              <ul className="mt-12 space-y-4">
                <ContactRow icon={<Phone size={18} />} label="Phone" value="021 607 073" href="tel:+64216070073" color="var(--ge-yellow)" />
                <ContactRow icon={<Mail size={18} />} label="Email" value="sales@goodeyes.co.nz" href="mailto:sales@goodeyes.co.nz" color="var(--ge-red)" />
                <ContactRow icon={<MapPin size={18} />} label="Studio" value="Auckland, New Zealand" color="var(--ge-green)" />
              </ul>

              <div className="mt-14 border-t border-[color:var(--ge-navy)]/10 pt-8">
                <GoodTagline height={80} />
              </div>
            </div>

            <div className="md:col-span-6">
              <form
                onSubmit={submit}
                className="rounded-2xl border border-[color:var(--ge-navy)]/10 bg-[color:var(--ge-cream)] p-6 md:p-8"
                data-testid="contact-form"
              >
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">
                  Send a message
                </div>
                <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: "var(--ge-yellow)" }} />

                <div className="mt-6 grid grid-cols-1 gap-4">
                  <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="contact-name" />
                  <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="contact-email" />
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">Subject</label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="mt-2 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-4 py-3 text-sm text-[color:var(--ge-navy)]"
                      data-testid="contact-subject"
                    >
                      <option>General enquiry</option>
                      <option>Become a stockist</option>
                      <option>Product question</option>
                      <option>Press &amp; media</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">Message</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={5}
                      className="mt-2 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-4 py-3 text-sm text-[color:var(--ge-navy)] resize-y"
                      data-testid="contact-message"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-6 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors"
                  >
                    Send message <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <BrandStripe height={22} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const ContactRow = ({ icon, label, value, href, color }) => {
  const inner = (
    <div className="flex items-center gap-4">
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full text-white" style={{ background: color }}>
        {icon}
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/60">{label}</div>
        <div className="mt-1 text-lg font-bold text-[color:var(--ge-navy)]">{value}</div>
      </div>
    </div>
  );
  return href ? <li><a href={href} className="block hover:opacity-90 transition-opacity">{inner}</a></li> : <li>{inner}</li>;
};

const Field = ({ label, value, onChange, tid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="mt-2 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-4 py-3 text-sm text-[color:var(--ge-navy)]"
    />
  </div>
);
